import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  HStack,
  VStack,
  Text,
  Badge,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Skeleton,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Tooltip,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import {
  CheckCircle,
  Warning,
  XCircle,
  Robot,
  Clock,
  ListBullets,
  MagnifyingGlass,
  Timer,
} from 'phosphor-react';
import { useQuery } from '@tanstack/react-query';
import { useMonitoramento, ILogBot, TipoBot } from '../../../hooks/useMonitoramento';
import { BotAreaBadge } from './BotAreaBadge';
import { TIPO_BOT_COLORS } from '../constants/botAreas';
import { PaginatorLocal } from './PaginatorLocal';
import {
  formatDate,
  getTaxaColor,
  getFlagColor,
  formatDuracao,
  getStatusColor,
} from '../utils/formatters';

const STATUS_ICONS: Record<string, any> = { ok: CheckCircle, warning: Warning, error: XCircle };

interface BotDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  botName: string;
  tipoBot: TipoBot;
  defaultTab?: number;
  defaultExecFilter?: string;
}

/**
 * Drill-down de um bot a partir do navegador de logs: execucoes recentes + logs
 * filtraveis. Usa SO as rotas de logging (`/orquestrador/logs/*`) — a visao
 * analitica por bot/empresa (rotas `/analise/bot/*`) saiu junto com os
 * dashboards antigos, substituidos pela tela /orquestrador.
 */
export const BotDetailDrawer: React.FC<BotDetailDrawerProps> = ({
  isOpen,
  onClose,
  botName,
  tipoBot,
  defaultTab,
  defaultExecFilter,
}) => {
  const [logsPage, setLogsPage] = useState(1);
  const [activeTab, setActiveTab] = useState(defaultTab ?? 0);
  const [selectedExecId, setSelectedExecId] = useState<string | null>(defaultExecFilter || null);
  const [execPage, setExecPage] = useState(1);
  const [execSize, setExecSize] = useState(20);
  const [logsSize, setLogsSize] = useState(30);
  const [logsSearch, setLogsSearch] = useState('');

  const {
    getLogsRPA,
    getLogsETL,
    getLogsWebScrap,
    getExecucoesRPA,
    getExecucoesETL,
    getExecucoesWebScrap,
  } = useMonitoramento();

  // Execucoes com paginacao
  const execFetcherMap = {
    RPA: getExecucoesRPA,
    ETL: getExecucoesETL,
    WEBSCRAP: getExecucoesWebScrap,
  };
  const { data: execucoesResp, isLoading } = useQuery({
    queryKey: ['botModalExecucoes', tipoBot, botName, execPage, execSize],
    queryFn: () => execFetcherMap[tipoBot](botName, execPage, execSize),
    enabled: isOpen && !!botName,
    staleTime: 30_000,
  });
  const execucoes = execucoesResp?.data ?? [];
  const execPagination = execucoesResp?.pagination;

  // Logs
  const logsFetcherMap = { RPA: getLogsRPA, ETL: getLogsETL, WEBSCRAP: getLogsWebScrap };
  const logsFetcher = logsFetcherMap[tipoBot];

  const logsFilters = useMemo(
    () => ({
      page: logsPage,
      size: logsSize,
      bot_name: botName,
      ...(selectedExecId ? { exec_id: selectedExecId } : {}),
      ...(logsSearch ? { content_line: logsSearch } : {}),
    }),
    [logsPage, logsSize, botName, selectedExecId, logsSearch],
  );

  const { data: logsResp, isLoading: logsLoading } = useQuery({
    queryKey: ['botModalLogs', logsFilters, tipoBot],
    queryFn: logsFetcher,
    enabled: isOpen && !!botName,
  });

  const logs: ILogBot[] = logsResp?.data ?? [];
  const logsPagination = logsResp?.pagination;

  // Handler: click exec → go to logs tab filtered
  const handleExecClick = (execId: string) => {
    setSelectedExecId(execId);
    setLogsPage(1);
    setActiveTab(1); // switch to Logs tab
  };

  // KPIs — derivados da pagina de execucoes carregada (o endpoint de analise
  // agregada por bot foi removido junto com os dashboards antigos)
  const kpis = useMemo(() => {
    const totalExecs = execPagination?.total || execucoes.length;
    const totalErros = execucoes.reduce((acc, e) => acc + (e.total_errors || 0), 0);
    const totalAvisos = execucoes.reduce((acc, e) => acc + (e.total_warnings || 0), 0);
    let taxa = 100;
    if (execucoes.length > 0) {
      const execsComErro = execucoes.filter(e => e.status === 'error').length;
      taxa = Math.round(((execucoes.length - execsComErro) / execucoes.length) * 100);
    }

    // Tempo medio de execucao — pagina carregada (servidor nao expoe duracao agregada)
    let tempoMedio = 0;
    const execsComTempo = execucoes.filter(e => e.date_init && (e.date_end || e.last_ts));
    if (execsComTempo.length > 0) {
      const totalMs = execsComTempo.reduce((acc, e) => {
        const start = new Date(e.date_init!).getTime();
        const end = new Date((e.date_end || e.last_ts)!).getTime();
        return acc + Math.max(0, end - start);
      }, 0);
      tempoMedio = Math.round(totalMs / execsComTempo.length / 1000); // segundos
    }

    return { execucoes: totalExecs, taxa, erros: totalErros, avisos: totalAvisos, tempoMedio };
  }, [execucoes, execPagination]);

  // KPIs contextuais para aba Logs
  const logsKpis = useMemo(() => {
    // Se tem exec selecionado, usar dados da exec (totais corretos)
    const selectedExec = selectedExecId ? execucoes.find(e => e.exec_id === selectedExecId) : null;

    if (selectedExec) {
      const total = selectedExec.total_logs || 0;
      const errors = selectedExec.total_errors || 0;
      const warnings = selectedExec.total_warnings || 0;
      return {
        total,
        errors,
        errorsPercent: total > 0 ? Math.round((errors / total) * 100) : 0,
        warnings,
        warningsPercent: total > 0 ? Math.round((warnings / total) * 100) : 0,
        successPercent: total > 0 ? Math.round(((total - errors - warnings) / total) * 100) : 100,
        empresas: selectedExec.total_empresas || 0,
      };
    }

    // Sem filtro: somar de todas as execucoes
    const totalLogs = execucoes.reduce((acc, e) => acc + (e.total_logs || 0), 0);
    const totalErrors = execucoes.reduce((acc, e) => acc + (e.total_errors || 0), 0);
    const totalWarnings = execucoes.reduce((acc, e) => acc + (e.total_warnings || 0), 0);
    const empresasSet = new Set(logs.map(l => l.codi_emp).filter(Boolean));
    return {
      total: totalLogs,
      errors: totalErrors,
      errorsPercent: totalLogs > 0 ? Math.round((totalErrors / totalLogs) * 100) : 0,
      warnings: totalWarnings,
      warningsPercent: totalLogs > 0 ? Math.round((totalWarnings / totalLogs) * 100) : 0,
      successPercent:
        totalLogs > 0
          ? Math.round(((totalLogs - totalErrors - totalWarnings) / totalLogs) * 100)
          : 100,
      empresas: empresasSet.size,
    };
  }, [logs, execucoes, selectedExecId]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh" borderRadius="xl">
        <ModalCloseButton />
        <ModalHeader borderBottomWidth="1px" pb={3}>
          <HStack spacing={2}>
            <Icon as={Robot} boxSize={5} color="blue.500" />
            <Text fontSize="lg" fontWeight="bold">
              {botName}
            </Text>
            <Badge colorScheme={TIPO_BOT_COLORS[tipoBot] || 'blue'} fontSize="xs">
              {tipoBot}
            </Badge>
            <BotAreaBadge botName={botName} />
          </HStack>
        </ModalHeader>

        <ModalBody p={0}>
          {isLoading ? (
            <VStack spacing={3} p={6}>
              {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} h="40px" w="100%" />
              ))}
            </VStack>
          ) : (
            <Box>
              {/* Tabs */}
              <Tabs
                variant="line"
                colorScheme="blue"
                size="sm"
                isLazy
                index={activeTab}
                onChange={setActiveTab}
              >
                <TabList px={4}>
                  <Tab gap={1}>
                    <Icon as={ListBullets} boxSize={3.5} /> Execucoes
                  </Tab>
                  <Tab gap={1}>
                    <Icon as={Clock} boxSize={3.5} /> Logs
                  </Tab>
                </TabList>
                <TabPanels>
                  {/* Execucoes Tab */}
                  <TabPanel p={0}>
                    <SimpleGrid
                      columns={4}
                      spacing={2}
                      px={4}
                      py={3}
                      bg="gray.50"
                      borderBottom="1px solid"
                      borderColor="gray.100"
                    >
                      <Stat size="sm" textAlign="center">
                        <StatLabel fontSize="10px">Execucoes</StatLabel>
                        <StatNumber fontSize="sm">{kpis.execucoes}</StatNumber>
                      </Stat>
                      <Stat
                        size="sm"
                        textAlign="center"
                        cursor="pointer"
                        onClick={() => {
                          setActiveTab(1);
                          setSelectedExecId(null);
                        }}
                        _hover={{ bg: 'red.50' }}
                        borderRadius="md"
                      >
                        <StatLabel fontSize="10px">Erros</StatLabel>
                        <StatNumber fontSize="sm" color="red.500">
                          {kpis.erros}
                        </StatNumber>
                      </Stat>
                      <Stat size="sm" textAlign="center">
                        <StatLabel fontSize="10px">Sucesso</StatLabel>
                        <StatNumber fontSize="sm" color={getTaxaColor(kpis.taxa)}>
                          {kpis.taxa}%
                        </StatNumber>
                      </Stat>
                      <Stat size="sm" textAlign="center">
                        <HStack justify="center" spacing={1}>
                          <Icon as={Timer} boxSize={3} color="gray.400" />
                          <StatLabel fontSize="10px">Tempo Medio</StatLabel>
                        </HStack>
                        <StatNumber fontSize="sm" color="gray.500">
                          {kpis.tempoMedio > 0 ? formatDuracao(kpis.tempoMedio) : '-'}
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>
                    <Box overflowX="auto">
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th w="40px">Status</Th>
                            <Th>Exec ID</Th>
                            <Th>Inicio</Th>
                            <Th>Fim</Th>
                            <Th isNumeric>Logs</Th>
                            <Th isNumeric>Erros</Th>
                            <Th>Empresas</Th>
                            <Th>Ultimo Log</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {execucoes && execucoes.length > 0 ? (
                            execucoes.map(exec => {
                              const endDate = exec.date_end || exec.last_ts;
                              const StatusIcon = STATUS_ICONS[exec.status] || CheckCircle;
                              return (
                                <Tr
                                  key={exec.exec_id}
                                  _hover={{ bg: 'blue.50' }}
                                  cursor="pointer"
                                  onClick={() => handleExecClick(exec.exec_id)}
                                >
                                  <Td>
                                    <Icon
                                      as={StatusIcon}
                                      color={`${getStatusColor(exec.status)}.500`}
                                      weight="fill"
                                      boxSize={4}
                                    />
                                  </Td>
                                  <Td>
                                    <Text fontSize="xs" fontFamily="mono" color="gray.500">
                                      {exec.exec_id?.slice(0, 8) || '-'}
                                    </Text>
                                  </Td>
                                  <Td>
                                    <HStack spacing={1}>
                                      <Badge
                                        colorScheme={getFlagColor(exec.first_flag || 'INFO')}
                                        fontSize="9px"
                                      >
                                        {exec.first_flag || '-'}
                                      </Badge>
                                      <Text fontSize="xs">{formatDate(exec.date_init)}</Text>
                                    </HStack>
                                  </Td>
                                  <Td>
                                    <HStack spacing={1}>
                                      <Badge
                                        colorScheme={getFlagColor(exec.last_flag || 'INFO')}
                                        fontSize="9px"
                                      >
                                        {exec.last_flag || '-'}
                                      </Badge>
                                      <Text fontSize="xs">{formatDate(endDate)}</Text>
                                    </HStack>
                                  </Td>
                                  <Td isNumeric fontSize="xs">
                                    {exec.total_logs ?? '-'}
                                  </Td>
                                  <Td
                                    isNumeric
                                    fontSize="xs"
                                    color={(exec.total_errors || 0) > 0 ? 'red.500' : undefined}
                                  >
                                    {exec.total_errors ?? '-'}
                                  </Td>
                                  <Td fontSize="xs">
                                    {exec.total_empresas > 0 ? (
                                      <HStack spacing={1}>
                                        <Text>{exec.total_empresas}</Text>
                                        {exec.total_empresas_erro > 0 && (
                                          <Badge colorScheme="red" fontSize="9px">
                                            {exec.total_empresas_erro} falha
                                          </Badge>
                                        )}
                                        {exec.total_empresas_warning > 0 && (
                                          <Badge colorScheme="orange" fontSize="9px">
                                            {exec.total_empresas_warning} aviso
                                          </Badge>
                                        )}
                                      </HStack>
                                    ) : (
                                      '-'
                                    )}
                                  </Td>
                                  <Td>
                                    <Tooltip label={exec.last_content} hasArrow placement="top">
                                      <Text fontSize="xs" noOfLines={1} maxW="250px">
                                        {exec.last_content || '-'}
                                      </Text>
                                    </Tooltip>
                                  </Td>
                                </Tr>
                              );
                            })
                          ) : (
                            <Tr>
                              <Td colSpan={8} textAlign="center" color="gray.400" py={8}>
                                Nenhuma execucao encontrada
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                    {/* Paginacao + seletor de tamanho */}
                    <HStack
                      px={4}
                      py={2}
                      justify="space-between"
                      borderTop="1px solid"
                      borderColor="gray.100"
                    >
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.500">
                          Exibir
                        </Text>
                        <Select
                          size="xs"
                          w="70px"
                          value={execSize}
                          onChange={e => {
                            setExecSize(Number(e.target.value));
                            setExecPage(1);
                          }}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </Select>
                        <Text fontSize="xs" color="gray.500">
                          por pagina
                        </Text>
                      </HStack>
                      {execPagination && (
                        <PaginatorLocal
                          page={execPagination.page}
                          pages={execPagination.total_pages}
                          total={execPagination.total}
                          hasNext={execPagination.has_next}
                          hasPrevious={execPagination.has_previous}
                          onPageChange={setExecPage}
                          label="execucoes"
                        />
                      )}
                    </HStack>
                  </TabPanel>

                  {/* Logs Tab */}
                  <TabPanel p={0}>
                    <SimpleGrid
                      columns={6}
                      spacing={2}
                      px={4}
                      py={3}
                      bg="gray.50"
                      borderBottom="1px solid"
                      borderColor="gray.100"
                    >
                      <Stat size="sm" textAlign="center">
                        <StatLabel fontSize="10px">Empresas</StatLabel>
                        <StatNumber fontSize="sm" color="blue.500">
                          {logsKpis.empresas}
                        </StatNumber>
                      </Stat>
                      <Stat size="sm" textAlign="center">
                        <StatLabel fontSize="10px">
                          {selectedExecId ? 'Exec ID' : 'Execucoes'}
                        </StatLabel>
                        <StatNumber fontSize="sm">
                          {selectedExecId ? (
                            <Text fontSize="xs" fontFamily="mono">
                              {selectedExecId.slice(0, 8)}
                            </Text>
                          ) : (
                            kpis.execucoes
                          )}
                        </StatNumber>
                      </Stat>
                      <Stat size="sm" textAlign="center">
                        <StatLabel fontSize="10px">Logs</StatLabel>
                        <StatNumber fontSize="sm" color="gray.500">
                          {logsKpis.total}
                        </StatNumber>
                      </Stat>
                      <Stat size="sm" textAlign="center">
                        <StatLabel fontSize="10px">Erros ({logsKpis.errorsPercent}%)</StatLabel>
                        <StatNumber fontSize="sm" color="red.500">
                          {logsKpis.errors}
                        </StatNumber>
                      </Stat>
                      <Stat size="sm" textAlign="center">
                        <StatLabel fontSize="10px">Avisos ({logsKpis.warningsPercent}%)</StatLabel>
                        <StatNumber fontSize="sm" color="orange.500">
                          {logsKpis.warnings}
                        </StatNumber>
                      </Stat>
                      <Stat size="sm" textAlign="center">
                        <StatLabel fontSize="10px">Sucesso</StatLabel>
                        <StatNumber fontSize="sm" color={getTaxaColor(logsKpis.successPercent)}>
                          {logsKpis.successPercent}%
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>
                    {/* Exec filter bar */}
                    <HStack
                      px={4}
                      py={2}
                      bg="gray.50"
                      borderBottom="1px solid"
                      borderColor="gray.100"
                      spacing={2}
                    >
                      <Select
                        size="xs"
                        maxW="280px"
                        placeholder="Todas as execucoes"
                        value={selectedExecId || ''}
                        onChange={e => {
                          setSelectedExecId(e.target.value || null);
                          setLogsPage(1);
                        }}
                      >
                        {(execucoes || []).map(ex => (
                          <option key={ex.exec_id} value={ex.exec_id}>
                            {formatDate(ex.date_init)} — {ex.total_logs} logs —{' '}
                            {ex.status?.toUpperCase()}
                          </option>
                        ))}
                      </Select>
                      {selectedExecId && (
                        <Badge
                          colorScheme="blue"
                          cursor="pointer"
                          onClick={() => {
                            setSelectedExecId(null);
                            setLogsPage(1);
                          }}
                          fontSize="xs"
                        >
                          Limpar filtro x
                        </Badge>
                      )}
                      <InputGroup size="xs" maxW="200px">
                        <InputLeftElement>
                          <MagnifyingGlass size={12} />
                        </InputLeftElement>
                        <Input
                          placeholder="Buscar no conteudo..."
                          value={logsSearch}
                          onChange={e => {
                            setLogsSearch(e.target.value);
                            setLogsPage(1);
                          }}
                        />
                      </InputGroup>
                      {logsSearch && (
                        <Badge
                          colorScheme="gray"
                          cursor="pointer"
                          onClick={() => {
                            setLogsSearch('');
                            setLogsPage(1);
                          }}
                          fontSize="xs"
                        >
                          x
                        </Badge>
                      )}
                    </HStack>
                    <Box overflowX="auto">
                      {logsLoading ? (
                        <VStack spacing={2} p={4}>
                          {[1, 2, 3].map(i => (
                            <Skeleton key={i} h="30px" w="100%" />
                          ))}
                        </VStack>
                      ) : (
                        <Table size="sm">
                          <Thead>
                            <Tr>
                              <Th>Timestamp</Th>
                              <Th>Flag</Th>
                              <Th>Empresa</Th>
                              <Th>Mensagem</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {logs.map(log => (
                              <Tr
                                key={`${log.exec_id}-${log.timestamp}-${log.content_line?.slice(0, 10)}`}
                                bg={
                                  { ERROR: 'red.50', WARNING: 'orange.50' }[log.flag as string] ||
                                  undefined
                                }
                                _hover={{
                                  bg:
                                    { ERROR: 'red.100', WARNING: 'orange.100' }[
                                      log.flag as string
                                    ] || 'gray.50',
                                }}
                              >
                                <Td fontSize="xs" whiteSpace="nowrap">
                                  {formatDate(log.timestamp)}
                                </Td>
                                <Td>
                                  <Badge colorScheme={getFlagColor(log.flag)} fontSize="9px">
                                    {log.flag}
                                  </Badge>
                                </Td>
                                <Td fontSize="xs">{log.codi_emp || '-'}</Td>
                                <Td>
                                  <Tooltip label={log.content_line} hasArrow placement="top">
                                    <Text fontSize="xs" noOfLines={2} maxW="300px">
                                      {log.content_line}
                                    </Text>
                                  </Tooltip>
                                </Td>
                              </Tr>
                            ))}
                            {logs.length === 0 && (
                              <Tr>
                                <Td colSpan={4} textAlign="center" color="gray.400" py={8}>
                                  Nenhum log encontrado
                                </Td>
                              </Tr>
                            )}
                          </Tbody>
                        </Table>
                      )}
                      <HStack
                        px={4}
                        py={2}
                        justify="space-between"
                        borderTop="1px solid"
                        borderColor="gray.100"
                      >
                        <HStack spacing={2}>
                          <Text fontSize="xs" color="gray.500">
                            Exibir
                          </Text>
                          <Select
                            size="xs"
                            w="70px"
                            value={logsSize}
                            onChange={e => {
                              setLogsSize(Number(e.target.value));
                              setLogsPage(1);
                            }}
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={30}>30</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </Select>
                          <Text fontSize="xs" color="gray.500">
                            por pagina
                          </Text>
                        </HStack>
                        {logsPagination && (
                          <PaginatorLocal
                            page={logsPagination.page}
                            pages={logsPagination.total_pages}
                            total={logsPagination.total}
                            hasNext={logsPagination.has_next}
                            hasPrevious={logsPagination.has_previous}
                            onPageChange={setLogsPage}
                            label="logs"
                          />
                        )}
                      </HStack>
                    </Box>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
